'use client';

/* -------------------------------------------------------------------------- */
/*                                Dependencies                                */
/* -------------------------------------------------------------------------- */

// Lib dependencies
import { useReactiveVar } from '@apollo/client';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

// UI lib components
import { Group, Button, Text } from '@mantine/core';

// UI local components
import UploadArea from '@ui-kit/molecules/UploadArea';
import PageShell from '@ui-kit/organisms/PageShell';
import ResourcePath from '@features/Resource/ResourcePath';
import ResourcePane from '@features/Resource/ResourcePane';
import ButtonCreateFolder from '@features/Resource/ButtonCreateFolder';
import ResourceMenu from '@features/Resource/ResourceMenu';
import useCanManageResources from '@features/Resource/hooks/useCanManageResources';
import SidePanelLayout from '@app/layouts/WorkspaceLayout/SidePanelLayout';
import { showNotification } from '@mantine/notifications';

// Context
import useWorkspace from '@features/Workspace/store/hooks/useWorkspace';

// Account
import useAccount from '@features/Account/store/useAccount';

// Store
import dialogStoreVar from '@features/Dialog/store/dialogStore';

// Services
import useUploader from '@services/upload/useUploader';

// API & Types
import {
  Resource,
  ResourceRowFragmentFragment,
  ResourceType,
  useExportWorkspaceResourcesJobMutation,
  useGetOneResourceQuery,
} from '@app-types/blackroom-api';
import { ResourceScope } from '@app-types/blackroom-app';
import { PageResourcesProps } from './types';

// Assets
import {
  IconUpload as IconUploadFiles,
  IconFileExport,
} from '@tabler/icons-react';

// Style
import { FONT_WEIGHT } from '@app/shared/ui-kit/fonts';
import { useEffect, useState } from 'react';
import { useSidePanel } from '@app/layouts/WorkspaceLayout/SidePanelLayout/store/sidePanelState';
import * as classes from './styles/index.css';

/* -------------------------------------------------------------------------- */
/*                                  Component                                 */
/* -------------------------------------------------------------------------- */

/**
 * Page showing user files
 */
function PageResources({ ...props }: PageResourcesProps) {
  /* ********************************** CONTEXT ********************************* */

  const { t } = useTranslation([
    'Common',
    'Group',
    'Redaction',
    'Insight',
    'Translation',
    'Activity',
    'Support',
  ]);
  const workspace = useWorkspace();
  const { isWorkspaceAdmin } = useAccount();
  const dialogStore = useReactiveVar(dialogStoreVar);
  const [activeResource, setActiveResource] =
    useState<ResourceRowFragmentFragment>();
  const { uploadSelectedResources } = useUploader();
  const { scope = 'my', folderId } = useParams<{
    scope: ResourceScope;
    folderId: string;
  }>();

  const sidePanel = useSidePanel();

  /* ********************************** LOCAL ********************************** */

  const { data: parentData } = useGetOneResourceQuery({
    variables: { workspaceId: workspace.id, resourceId: folderId as string },
    skip: !folderId,
    fetchPolicy: 'cache-and-network',
  });

  // Fetch full resource data for the side panel menu
  const { data: activeResourceData } = useGetOneResourceQuery({
    variables: {
      workspaceId: workspace.id,
      resourceId: activeResource?.id as string,
    },
    skip: !activeResource?.id,
  });

  const [exportWorkspaceResourcesJob] =
    useExportWorkspaceResourcesJobMutation();

  const parent = parentData?.resource as Resource | undefined;
  const fullActiveResource = activeResourceData?.resource;

  /* ******************************** CALLBACKS ******************************* */

  /* ******************************* EFFECTS ******************************* */

  useEffect(() => {
    sidePanel.close();
  }, [scope]);

  useEffect(() => {
    if (activeResource && sidePanel.state === 'collapsed') {
      sidePanel.open();
    } else if (!activeResource && sidePanel.state === 'expanded') {
      sidePanel.close();
    }
  }, [activeResource]);

  /* ***************************** RENDER HELPERS ***************************** */

  const { canManageResourcesHere } = useCanManageResources();

  function getCreateResourceActions() {
    if (
      !(['recent', 'favorites', 'archived'] as ResourceScope[]).includes(scope)
    ) {
      return (
        <Group className={classes.resourceActions} wrap="nowrap" gap={8}>
          {canManageResourcesHere && (
            <>
              <ButtonCreateFolder />
              <Button
                size="sm"
                radius="xs"
                px="sm"
                flex={1}
                miw={170}
                variant="filled"
                color="blackroomBlue"
                leftSection={<IconUploadFiles size={18} />}
                onClick={() =>
                  dialogStore?.invokeDialog?.('dialog-resource-upload', {
                    modalProps: {
                      withCloseButton: false,
                      title: t('Resource:DialogResourceUpload.title'),
                    },
                  })
                }
              >
                <Text fw={FONT_WEIGHT.bold}> {t('Common:cta.upload')}</Text>
              </Button>
            </>
          )}
          <Button
            size="sm"
            radius="xs"
            px="sm"
            flex={1}
            miw={170}
            variant="light"
            color="blackroomBlack"
            leftSection={<IconFileExport size={18} />}
            onClick={async () => {
              await exportWorkspaceResourcesJob({
                variables: {
                  workspaceId: workspace.id,
                },
              })
                .then(() => {
                  showNotification({
                    title: t('Resource:Common.notifications.export.title'),
                    message: t('Resource:Common.notifications.export.message'),
                    color: 'blackroomGreen',
                  });
                })
                .catch(() => {
                  showNotification({
                    title: t('Common:errors.genericTitle'),
                    message: t('Common:errors.genericMessage'),
                    color: 'blackroomRed',
                  });
                });
            }}
          >
            <Text fw={FONT_WEIGHT.bold}>
              {t('Resource:ResourcePane.toolbar.export')}
            </Text>
          </Button>
        </Group>
      );
    }
    return null;
  }

  /* ******************************** RENDERING ******************************* */

  return (
    <SidePanelLayout
      controlled
      sidePanelContent={
        <ResourceMenu
          resource={fullActiveResource}
          prefilledResource={activeResource}
          allowedSections={
            isWorkspaceAdmin(workspace)
              ? ['activities', 'permissions', 'redact', 'metadata']
              : ['support', 'translation']
          }
          resetAfterDelete={() => {
            setActiveResource(undefined);
          }}
        />
      }
    >
      <PageShell
        title={
          <ResourcePath
            parentId={parent?.id}
            scope={scope}
            navigation="page"
            withRootElement
            showRootElementAtRoot
            currentFolderEditable={
              canManageResourcesHere && parent?.type === ResourceType.Folder
            }
          />
        }
        actions={getCreateResourceActions()}
        w="auto"
        flex={1}
        gap={
          (['recent', 'favorites', 'archived'] as ResourceScope[]).includes(
            scope,
          )
            ? 0
            : undefined
        }
      >
        <ResourcePane
          activeResource={activeResource}
          scope={scope}
          parent={parent}
          onClickResource={(r) => {
            if (activeResource && (!r || r.type === ResourceType.Folder)) {
              setActiveResource(undefined);
              return;
            }

            if (r?.type === ResourceType.File) {
              setActiveResource(r);
            }
          }}
        />
        {dialogStore?.dialogIdentifier || !canManageResourcesHere ? null : (
          <UploadArea
            onAddedFiles={async (files) => {
              await uploadSelectedResources({ files, parent });
            }}
            asPageUploader
          />
        )}
      </PageShell>
    </SidePanelLayout>
  );
}

export default PageResources;
